
import { Category, Question } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';

interface QuestionHandlersProps {
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  selectedCategory: Category | undefined;
  setIsAddingQuestion: (value: boolean) => void;
  setEditingQuestionId: (id: string | null) => void;
  onContentChange?: () => void; // Add callback for content changes
}

export function useQuestionHandlers({
  categories,
  setCategories,
  selectedCategory,
  setIsAddingQuestion,
  setEditingQuestionId,
  onContentChange
}: QuestionHandlersProps) {
  const { toast } = useToast();

  // Handler for adding a new question
  const handleAddQuestion = () => {
    if (!selectedCategory) {
      toast({
        title: "No category selected",
        description: "Please select a category first.",
        variant: "destructive"
      });
      return;
    }
    
    setIsAddingQuestion(true);
    setEditingQuestionId(null);
  };

  // Handler for editing an existing question
  const handleEditQuestion = (questionId: string) => {
    setEditingQuestionId(questionId);
    setIsAddingQuestion(false);
  };

  // Handler for closing question form
  const handleCloseQuestionForm = () => {
    setIsAddingQuestion(false);
    setEditingQuestionId(null);
  };

  // Handler for saving a question (both new and edited)
  const handleSaveQuestion = (categoryId: string, questionData: Partial<Question>) => {
    if (!questionData.text?.trim()) {
      toast({
        title: "Invalid question",
        description: "Question text is required.",
        variant: "destructive"
      });
      return;
    }

    setCategories(prevCategories => 
      prevCategories.map(category => {
        if (category.id === categoryId) {
          if (questionData.id) {
            // Update existing question
            return {
              ...category,
              questions: category.questions.map(question =>
                question.id === questionData.id
                  ? { ...question, ...questionData } as Question
                  : question
              )
            };
          } else {
            // Add new question
            const newQuestion: Question = {
              id: crypto.randomUUID(),
              text: questionData.text,
              explanation: questionData.explanation,
              answerType: questionData.answerType || 'single',
              answers: questionData.answers || [
                { id: crypto.randomUUID(), text: 'Yes', value: 1 },
                { id: crypto.randomUUID(), text: 'No', value: 0 }
              ],
              required: questionData.required ?? true,
              premium: questionData.premium ?? false,
              disabled: questionData.disabled ?? false
            };

            return {
              ...category,
              questions: [...category.questions, newQuestion]
            };
          }
        }
        return category;
      })
    );

    // Close the form
    setIsAddingQuestion(false);
    setEditingQuestionId(null);

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100); // Small delay to let state update
    }

    toast({
      title: "Success",
      description: questionData.id ? "Question updated successfully" : "Question added successfully"
    });
  };

  // Handler for deleting a question
  const handleDeleteQuestion = (categoryId: string, questionId: string) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.filter(question => question.id !== questionId)
            }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }

    toast({
      title: "Question deleted",
      description: "The question has been permanently removed."
    });
  };

  // Handler for toggling question disabled status
  const handleToggleQuestionStatus = (categoryId: string, questionId: string) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? { ...question, disabled: !question.disabled } 
                  : question
              )
            }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }

    const question = categories
      .find(cat => cat.id === categoryId)
      ?.questions.find(q => q.id === questionId);

    toast({
      title: question?.disabled ? "Question enabled" : "Question disabled",
      description: question?.disabled 
        ? "The question is now available in assessments." 
        : "The question has been hidden from new assessments."
    });
  };

  // Handler for drag and drop reordering
  const handleDragEnd = (result: any) => {
    if (!result.destination || !selectedCategory) {
      return;
    }

    const items = Array.from(selectedCategory.questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategories(prevCategories =>
      prevCategories.map(category =>
        category.id === selectedCategory.id
          ? { ...category, questions: items }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }
  };

  // Handler for updating a question
  const handleUpdateQuestion = (categoryId: string, questionId: string, updates: any) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? { ...question, ...updates } 
                  : question
              )
            }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }
  };

  // Handler for updating an answer
  const handleUpdateAnswer = (categoryId: string, questionId: string, answerId: string, updates: any) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? {
                      ...question,
                      answers: question.answers.map(answer => 
                        answer.id === answerId 
                          ? { ...answer, ...updates } 
                          : answer
                      )
                    } 
                  : question
              )
            }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }
  };

  // Handler for adding an answer
  const handleAddAnswer = (categoryId: string, questionId: string) => {
    const newAnswer = {
      id: crypto.randomUUID(),
      text: 'New Answer',
      value: 0
    };

    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? {
                      ...question,
                      answers: [...question.answers, newAnswer]
                    } 
                  : question
              )
            }
          : category
      )
    );

    // Notify parent about content change
    if (onContentChange) {
      setTimeout(onContentChange, 100);
    }
  };

  return {
    handleAddQuestion,
    handleEditQuestion,
    handleCloseQuestionForm,
    handleSaveQuestion,
    handleDeleteQuestion,
    handleToggleQuestionStatus,
    handleDragEnd,
    handleUpdateQuestion,
    handleUpdateAnswer,
    handleAddAnswer
  };
}
